/**
 * DateField — TREATX shared date input component.
 *
 * Sprint: TREATX-BATCH-1.2 D1 → TREATX-BATCH-1.2.1 D1 rebuild.
 *
 * Owner rule (GLOBAL DATE PRESENTATION FINAL CLOSURE):
 *   • Manually-entered dates are LOCALE-AWARE:
 *       TR → DD.MM.YYYY (placeholder gg.aa.yyyy)
 *       EN/global → MM/DD/YYYY (placeholder mm/dd/yyyy)
 *     Driven by the app i18n locale (NOT the browser/OS locale), because
 *     native `<input type="date">` cannot be steered by `lang` on Chromium.
 *   • Backend/API/DB storage remains canonical ISO (YYYY-MM-DD).
 *
 * Design:
 *   • The visible control is a MASKED TEXT INPUT that auto-inserts the
 *     locale separator ('.' for TR, '/' for EN) and constrains entry to
 *     the locale order (DD.MM.YYYY / MM/DD/YYYY).
 *   • `value` is accepted as ISO YYYY-MM-DD (canonical) OR a native `Date`.
 *   • `onChange(iso)` fires with the canonical ISO string when the
 *     user's typed value is a complete valid date; fires with
 *     `""` while incomplete so the caller can clear.
 *   • Optional `min` / `max` accepted as ISO strings — enforced
 *     on emit; invalid entries fall back to `""`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  formatDateLocale,
  parseDateLocale,
  dateSeparator,
  datePlaceholderText,
} from '../utils/dateFormat';

// re-exports retained for any legacy import site (now locale-aware).
export function formatDateEU(value) {
  return formatDateLocale(value);
}
export function parseDateEU(value) {
  return parseDateLocale(value);
}

// Masks the raw typed string into NN<sep>NN<sep>NNNN, auto-inserting `sep`.
// Non-numeric chars are stripped; anything past 8 digits is trimmed.
function _mask(raw, sep) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join(sep);
}

export function DateField({
  value,
  onChange,
  label,
  name,
  id,
  required = false,
  min,
  max,
  helperText,
  disabled = false,
  className = '',
  // P0-UI-DATEFIELD-HINT-01 — `className` styles the WRAPPER (input +
  // format hint). Callers that passed a fixed height here clipped the
  // wrapper and the hint spilled over the next row. Input-only styling
  // belongs on `inputClassName`.
  inputClassName = '',
  placeholder,
  'data-testid': testId,
  ...rest
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const sep = useMemo(() => dateSeparator(locale), [locale]);

  // Presentation string kept in local state so incomplete typing is
  // preserved without triggering a controlled-input value snap-back.
  const [display, setDisplay] = useState(() => formatDateLocale(value, locale));
  // TREATX-BATCH-1.2.1 P1 — track focus so the external `value` sync
  // never clobbers a mid-edit display. Prior behaviour: user hits
  // Backspace on a complete date → onChange('') fires → parent
  // clears state → effect re-runs → local `display` wiped, forcing
  // full retype.
  const [focused, setFocused] = useState(false);

  // Sync when the external value (or app locale) changes (initial load,
  // form reset, language switch) — but ONLY while the field is not edited.
  useEffect(() => {
    if (focused) return;
    const next = formatDateLocale(value, locale);
    setDisplay((cur) => (cur === next ? cur : next));
  }, [value, focused, locale]);

  const handleFocus = () => setFocused(true);

  const handleChange = (e) => {
    const masked = _mask(e.target.value, sep);
    setDisplay(masked);
    if (!onChange) return;
    if (masked === '') {
      onChange('');
      return;
    }
    const iso = parseDateLocale(masked, locale, { min, max });
    onChange(iso); // "" while incomplete, ISO once complete + valid
  };

  const handleBlur = () => {
    setFocused(false);
    // On blur, if the entered text isn't a valid date, either normalise
    // (re-render the canonical ISO in the locale format) or clear.
    if (!display) return;
    const iso = parseDateLocale(display, locale, { min, max });
    if (!iso) {
      setDisplay('');
      onChange && onChange('');
    } else {
      setDisplay(formatDateLocale(iso, locale));
    }
  };

  const displayId = id || (name ? `date-${name}` : undefined);
  const ph = placeholder ?? datePlaceholderText(locale);
  const hint = helperText ?? `${t('date.formatLabel', 'Format')}: ${datePlaceholderText(locale)}`;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <Label htmlFor={displayId} className="text-xs text-slate-600">
          {label}{required ? ' *' : ''}
        </Label>
      )}
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        name={name}
        id={displayId}
        required={required}
        disabled={disabled}
        placeholder={ph}
        maxLength={10}
        data-testid={testId}
        className={inputClassName}
        {...rest}
      />
      <p
        className="text-[10px] text-slate-400"
        data-testid={testId ? `${testId}-hint` : undefined}
      >
        {hint}
      </p>
    </div>
  );
}

export default DateField;
